import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Pagination
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingIcon from '@mui/icons-material/Pending';
import { getDataset, removeCommentFromDataset, getCommentContext, downloadDataset, processDatasetEmbeddings, getDatasetEmbeddingsStatus, resetDatasetEmbeddingsStatus, getDatasetEmbeddingsStats } from '../services/api';
import { Dataset, Comment } from '../types';
import { CommentCard } from '../components/CommentCard';
import { CodeContextViewer } from '../components/CodeContextViewer';

export const DatasetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeComment, setActiveComment] = useState<Comment | null>(null);
  const [notebookContent, setNotebookContent] = useState<string[] | null>(null);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [commentsPerPage] = useState(5);
  const [processingEmbeddings, setProcessingEmbeddings] = useState(false);
  const [statusPollingInterval, setStatusPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [embeddingsStats, setEmbeddingsStats] = useState<{
    totalBlocks: number;
    blocksWithEmbeddings: number;
    percentage: number;
  } | null>(null);

  // Загрузка датасета при монтировании компонента
  useEffect(() => {
    if (id) {
      fetchDataset(parseInt(id));
    }
    
    // Очистка интервала при размонтировании компонента
    return () => {
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
      }
    };
  }, [id]);

  // Функция для загрузки датасета
  const fetchDataset = async (datasetId: number) => {
    try {
      setLoading(true);
      const data = await getDataset(datasetId);
      setDataset(data);
      
      // Преобразуем комментарии из датасета в массив
      if (data.comments && Array.isArray(data.comments)) {
        setComments(data.comments);
      }
      
      // Если статус обработки эмбеддингов "processing", начинаем опрос статуса
      if (data.embeddingsStatus === 'processing') {
        startStatusPolling(datasetId);
      }
      
      // Загружаем статистику эмбеддингов
      if (data.embeddingsStatus) {
        fetchEmbeddingsStats(datasetId);
      }
      
      setError(null);
    } catch (err) {
      console.error('Ошибка при загрузке датасета:', err);
      setError('Не удалось загрузить датасет');
    } finally {
      setLoading(false);
    }
  };

  // Функция для загрузки статистики эмбеддингов
  const fetchEmbeddingsStats = async (datasetId: number) => {
    try {
      const stats = await getDatasetEmbeddingsStats(datasetId);
      
      if (stats) {
        setEmbeddingsStats({
          totalBlocks: stats.totalBlocks,
          blocksWithEmbeddings: stats.blocksWithEmbeddings,
          percentage: stats.percentage || 0
        });
      }
    } catch (error) {
      console.error('Ошибка при получении статистики эмбеддингов:', error);
    }
  };

  // Функция для удаления комментария из датасета
  const handleRemoveComment = async (commentId: number) => {
    if (!dataset) return;
    
    if (window.confirm('Вы уверены, что хотите удалить этот комментарий из датасета?')) {
      try {
        await removeCommentFromDataset(dataset.id, commentId);
        
        // Обновляем список комментариев
        setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
        
        // Если активный комментарий был удален, сбрасываем его
        if (activeComment && activeComment.id === commentId) {
          setActiveComment(null);
          setNotebookContent(null);
        }
      } catch (err) {
        console.error('Ошибка при удалении комментария из датасета:', err);
        setError('Не удалось удалить комментарий из датасета');
      }
    }
  };

  // Функция для возврата к списку датасетов
  const handleBackToList = () => {
    navigate('/datasets');
  };

  // Функция для открытия ноутбука с комментарием
  const handleOpenNotebook = (notebookId: number) => {
    navigate(`/notebooks/${notebookId}`);
  };

  // Функция для отображения комментария и загрузки контекста кода
  const handleViewComment = async (comment: Comment) => {
    setActiveComment(comment);
    setIsLoadingContext(true);
    setContextError(null);
    setNotebookContent([]);

    try {
      const contextData = await getCommentContext(comment.id, 5);
      if (contextData && contextData.context && Array.isArray(contextData.context.lines)) {
        const extractedLines = contextData.context.lines.map((line: any) => line.content || '');

        const lineNumbers = contextData.context.lines.map((line: any) => line.number);

        const cellType = contextData.context.cell_type || 'code';
        if (contextData.context.contextRange && typeof contextData.context.contextRange.highlight === 'number') {
          const highlightLineNumber = contextData.context.contextRange.highlight;

          const highlightedLineIndex = lineNumbers.findIndex((num: number) => num === highlightLineNumber);

          if (highlightedLineIndex >= 0) {
            // Получаем номер строки из API
            const highlightedLineNumber = lineNumbers[highlightedLineIndex];

            // Получаем номер первой строки из API
            const firstLineNumber = lineNumbers[0];

            const relativeIndex = highlightedLineIndex;

            setNotebookContent(extractedLines);
            
            // Обновляем активный комментарий с правильным номером строки из контекста
            setActiveComment(prev => {
              if (!prev) return null;
              const updatedComment = {
                ...prev,
                // Сохраняем оригинальный номер строки из комментария
                originalLineNumber: prev.lineNumber,
                // Устанавливаем номер строки для отображения в контексте (относительный индекс)
                // Добавляем +1 к индексу, чтобы компенсировать смещение в CodeContextViewer
                lineNumber: relativeIndex,
                // Сохраняем номера строк из API
                lineNumbers: lineNumbers,
                // Сохраняем тип ячейки
                cellType: cellType as 'code' | 'markdown'
              };
              return updatedComment;
            });
          } else {
            console.warn('Не найдена выделенная строка в контексте по номеру строки');
            fallbackToMiddleIndex(extractedLines, lineNumbers, cellType);
          }
        } else {
          // Проверяем, относится ли комментарий к первой строке (lineNumber = 0)
          // В API первая строка обычно имеет номер 1
          if (comment.lineNumber === 0) {
            // Ищем строку с номером 1 в API
            const firstLineIndex = lineNumbers.findIndex((num: number) => num === 1);
            
            if (firstLineIndex >= 0) {
              setNotebookContent(extractedLines);
              
              // Обновляем активный комментарий с правильным номером строки
              setActiveComment(prev => {
                if (!prev) return null;
                const updatedComment = {
                  ...prev,
                  originalLineNumber: prev.lineNumber,
                  // Устанавливаем индекс первой строки
                  lineNumber: 0, // Используем 0, чтобы выделить первую строку
                  lineNumbers: lineNumbers,
                  cellType: cellType as 'code' | 'markdown'
                };
                return updatedComment;
              });
              return;
            }
          }
          
          // Находим индекс строки с is_highlight=true
          const highlightedLineIndex = contextData.context.lines.findIndex((line: any) => line.is_highlight === true);

          if (highlightedLineIndex >= 0) {
            const highlightedLineNumber = contextData.context.lines[highlightedLineIndex].number;

            const firstLineNumber = contextData.context.lines[0].number;
            const relativeIndex = highlightedLineIndex;

            setNotebookContent(extractedLines);
            
            setActiveComment(prev => {
              if (!prev) return null;
              const updatedComment = {
                ...prev,
                // Сохраняем оригинальный номер строки из комментария
                originalLineNumber: prev.lineNumber,
                // Устанавливаем номер строки для отображения в контексте (относительный индекс)
                lineNumber: relativeIndex,
                // Сохраняем номера строк из API
                lineNumbers: lineNumbers,
                // Сохраняем тип ячейки
                cellType: cellType as 'code' | 'markdown'
              };
              return updatedComment;
            });
          } else {
            console.warn('Не найдена выделенная строка в контексте');
            fallbackToMiddleIndex(extractedLines, lineNumbers, cellType);
          }
        }
      } else {
        console.error('Неверный формат данных контекста:', contextData);
        setContextError('Не удалось загрузить контекст кода');
      }
    } catch (error) {
      console.error('Ошибка при загрузке контекста:', error);
      setContextError('Ошибка при загрузке контекста кода');
    } finally {
      setIsLoadingContext(false);
    }
  };

  // Вспомогательная функция для использования середины массива, если выделенная строка не найдена
  const fallbackToMiddleIndex = (extractedLines: string[], lineNumbers: number[], cellType: string) => {
    setNotebookContent(extractedLines);
    
    // Если выделенная строка не найдена, используем середину массива
    const middleIndex = Math.floor(extractedLines.length / 2);
    
    setActiveComment(prev => {
      if (!prev) return null;
      return {
        ...prev,
        originalLineNumber: prev.lineNumber,
        lineNumber: middleIndex,
        lineNumbers: lineNumbers,
        cellType: cellType as 'code' | 'markdown'
      };
    });
  };

  // Функция для перехода к предыдущему комментарию
  const handlePrevComment = () => {
    if (!activeComment || comments.length <= 1) return;
    
    const currentIndex = comments.findIndex(c => c.id === activeComment.id);
    if (currentIndex <= 0) return;
    
    const prevComment = comments[currentIndex - 1];
    
    // Вычисляем страницу, на которой находится предыдущий комментарий
    const commentPage = Math.ceil((currentIndex) / commentsPerPage);
    if (commentPage !== page) {
      setPage(commentPage);
    }
    
    handleViewComment(prevComment);
  };

  // Функция для перехода к следующему комментарию
  const handleNextComment = () => {
    if (!activeComment || comments.length <= 1) return;
    
    const currentIndex = comments.findIndex(c => c.id === activeComment.id);
    if (currentIndex >= comments.length - 1) return;
    
    const nextComment = comments[currentIndex + 1];
    
    // Вычисляем страницу, на которой находится следующий комментарий
    const commentPage = Math.ceil((currentIndex + 2) / commentsPerPage);
    if (commentPage !== page) {
      setPage(commentPage);
    }
    
    handleViewComment(nextComment);
  };

  // Обработчик нажатий клавиш для навигации
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeComment) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevComment();
          break;
        case 'ArrowRight':
          handleNextComment();
          break;
        case 'ArrowUp':
          handlePrevComment();
          break;
        case 'ArrowDown':
          handleNextComment();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeComment, comments, page]);

  // Вычисляем текущие комментарии для отображения
  const indexOfLastComment = page * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  const currentComments = comments.slice(indexOfFirstComment, indexOfLastComment);
  const totalPages = Math.ceil(comments.length / commentsPerPage);

  // Функция для скачивания датасета
  const handleDownloadDataset = async () => {
    if (!dataset) return;
    
    try {
      setLoading(true);
      await downloadDataset(dataset.id);
    } catch (err) {
      console.error('Ошибка при скачивании датасета:', err);
      setError('Не удалось скачать датасет');
    } finally {
      setLoading(false);
    }
  };

  // Функция для запуска обработки эмбеддингов
  const handleProcessEmbeddings = async () => {
    if (!dataset) return;
    
    try {
      setProcessingEmbeddings(true);
      const result = await processDatasetEmbeddings(dataset.id);
      
      // Обновляем датасет с новым статусом
      setDataset(prev => prev ? { ...prev, embeddingsStatus: result.embeddingsStatus } : null);
      
      // Если статус "processing", начинаем опрос статуса
      if (result.embeddingsStatus === 'processing') {
        startStatusPolling(dataset.id);
      }
      
    } catch (err) {
      console.error('Ошибка при запуске обработки эмбеддингов:', err);
      setError('Не удалось запустить обработку эмбеддингов');
    } finally {
      setProcessingEmbeddings(false);
    }
  };

  // Функция для запуска опроса статуса обработки эмбеддингов
  const startStatusPolling = (datasetId: number) => {
    // Останавливаем предыдущий опрос, если он был запущен
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
    }
    
    // Запускаем новый опрос каждые 5 секунд
    const interval = setInterval(async () => {
      try {
        const statusResult = await getDatasetEmbeddingsStatus(datasetId);
        
        // Обновляем датасет с новым статусом
        setDataset(prev => prev ? { ...prev, embeddingsStatus: statusResult.embeddingsStatus } : null);
        
        // Загружаем статистику эмбеддингов
        fetchEmbeddingsStats(datasetId);
        
        // Если статус больше не "processing", останавливаем опрос
        if (statusResult.embeddingsStatus !== 'processing') {
          clearInterval(interval);
          setStatusPollingInterval(null);
        }
      } catch (err) {
        console.error('Ошибка при получении статуса обработки эмбеддингов:', err);
        // Не останавливаем опрос при ошибке, продолжаем пытаться
      }
    }, 5000);
    
    setStatusPollingInterval(interval);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={handleBackToList} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {loading ? 'Загрузка...' : dataset?.title || 'Датасет не найден'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : dataset ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Информация о датасете
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadDataset}
                  disabled={loading || comments.length === 0}
                  size="small"
                >
                  Скачать
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {dataset.description && (
                <Typography variant="body1" paragraph>
                  {dataset.description}
                </Typography>
              )}
              
              <Typography variant="body2" color="textSecondary">
                Создан: {new Date(dataset.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Обновлен: {new Date(dataset.updatedAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Комментариев: {comments.length}
              </Typography>
              
              {/* Блок статуса эмбеддингов */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Статус эмбеддингов:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {/* Статус эмбеддингов */}
                  {(!dataset.embeddingsStatus || dataset.embeddingsStatus === 'not_started') && (
                    <Chip 
                      icon={<HourglassEmptyIcon />} 
                      label="Не начато" 
                      color="default" 
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {dataset.embeddingsStatus === 'processing' && (
                    <Chip 
                      icon={<PendingIcon />} 
                      label="Обработка" 
                      color="primary" 
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {dataset.embeddingsStatus === 'error' && (
                    <Chip 
                      icon={<ErrorIcon />} 
                      label="Ошибка" 
                      color="error" 
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {dataset.embeddingsStatus === 'done' && (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="Готово" 
                      color="success" 
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  
                  {/* Количество эмбеддингов */}
                  {embeddingsStats && (
                    embeddingsStats.blocksWithEmbeddings === embeddingsStats.totalBlocks ? (
                      <Chip 
                        label={`${embeddingsStats.blocksWithEmbeddings}/${embeddingsStats.totalBlocks} блоков`} 
                        color="success" 
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Chip 
                        label={dataset.embeddingsStatus === 'processing' 
                          ? `${embeddingsStats.blocksWithEmbeddings}/${embeddingsStats.totalBlocks} блоков (${embeddingsStats.percentage}%)` 
                          : `${embeddingsStats.blocksWithEmbeddings}/${embeddingsStats.totalBlocks} блоков`} 
                        color="warning" 
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )
                  )}
                  
                  {/* Иконка для расчёта эмбеддингов */}
                  <Tooltip title="Рассчитать эмбеддинги">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleProcessEmbeddings}
                        disabled={processingEmbeddings || dataset.embeddingsStatus === 'processing'}
                        size="small"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Комментарии в датасете
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {comments.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  В этом датасете нет комментариев
                </Typography>
              ) : (
                <>
                  <List>
                    {currentComments.map(comment => (
                      <ListItem 
                        key={comment.id}
                        button
                        onClick={() => handleViewComment(comment)}
                        selected={activeComment?.id === comment.id}
                        sx={{ 
                          borderRadius: 1,
                          mb: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(14, 27, 55, 0.1)',
                          }
                        }}
                      >
                        <ListItemText
                          primary={comment.text.length > 50 ? `${comment.text.substring(0, 50)}...` : comment.text}
                          secondary={`Ноутбук ID: ${comment.notebookId}`}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Удалить из датасета">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveComment(comment.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={(event, value) => setPage(value)} 
                        color="primary" 
                        size="small"
                      />
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            {activeComment ? (
              <Box>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Просмотр комментария
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={handlePrevComment}
                        disabled={comments.findIndex(c => c.id === activeComment.id) <= 0}
                        sx={{ minWidth: '40px', px: 1 }}
                      >
                        ←
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={handleNextComment}
                        disabled={comments.findIndex(c => c.id === activeComment.id) >= comments.length - 1}
                        sx={{ minWidth: '40px', px: 1 }}
                      >
                        →
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => handleOpenNotebook(activeComment.notebookId)}
                      >
                        Открыть ноутбук
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <CommentCard
                    comment={activeComment}
                    onClose={() => {
                      setActiveComment(null);
                      setNotebookContent(null);
                    }}
                    isReadOnly={true}
                    showNavigation={false}
                  />
                  
                  {isLoadingContext ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : contextError ? (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary" align="center">
                        {contextError}
                      </Typography>
                    </Box>
                  ) : notebookContent && notebookContent.length > 0 ? (
                    <Box sx={{ mt: 3 }}>
                      <>
                      <CodeContextViewer
                        code={notebookContent}
                        highlightLineIndex={activeComment.lineNumber}
                        contextLines={5}
                        lineNumbers={activeComment.lineNumbers}
                        cellType={activeComment.cellType as 'code' | 'markdown'}
                        hideTitle={true}
                      />
                      </>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Не удалось загрузить контекст кода для этого комментария
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  Выберите комментарий из списка слева для просмотра
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Датасет не найден
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            Вернуться к списку датасетов
          </Button>
        </Paper>
      )}
    </Container>
  );
}; 